import React, {
  useCallback,
  useMemo,
  useState
} from "react";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";

import ProductStatusRow from "src/core/products/components/ProductStatuses/ProductStatusRow";
import { ProductStatus } from "src/core/products/api/products.api";
import { ProductStatusRowType } from "src/core/products/components/ProductStatuses/FutureProductStatuses";
import { Text } from "src/library/components/Text";
import { Button } from "src/library/components/Button";

interface NewFutureStatusProps {
  productId: number;
  onSave: (newProductStatus: ProductStatusRowType) => void;
  onCancel: () => void;
}

/**
 * Returns an editable row to input a new product status.
 */
const NewFutureStatus: React.ReactFCWithChildren<NewFutureStatusProps> = ({
  productId,
  onSave,
  onCancel
}) => {
  const defaultNewProductStatus = useMemo(() => {
    return {
      productId,
      status: ProductStatus.Active,
      startDate: new Date()
    };
  }, [ productId ]);

  const [ newProductStatus, setNewProductStatus ] = useState<ProductStatusRowType>(defaultNewProductStatus);

  const handleSave = useCallback(() => {
    if (newProductStatus.status === ProductStatus.Suspended && !newProductStatus.endDate) {
      toast.error("End date required when setting a product status to suspended.");

      return;
    }

    // setting localId only at the point of saving new status in local state.
    onSave({
      ...newProductStatus,
      localId: uuid()
    });
    setNewProductStatus(defaultNewProductStatus);
  }, [ defaultNewProductStatus, newProductStatus ]);

  return (
    <>
      <Text
        as="h4"
        fontSize="h4"
      >
        Add Future Status
      </Text>

      <ProductStatusRow
        productId={productId}
        productStatus={newProductStatus}
        updateFutureStatuses={setNewProductStatus}
      />

      <ActionButtonsWrapper>
        <Button
          onClick={onCancel}
          $buttonType="secondary"
        >
          Cancel
        </Button>

        <Button
          className="save-new-status"
          onClick={() => handleSave()}
        >
          Save New Future Status
        </Button>
      </ActionButtonsWrapper>
    </>
  );
};

const ActionButtonsWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
  
    .save-new-status {
      margin-left: ${theme.spacing.margins.md}
    }
  `}
`;

export default NewFutureStatus;
